import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Badge,
  Fade,
  Zoom
} from '@mui/material';
import {
  SmartToy,
  Chat,
  Close,
  AutoAwesome
} from '@mui/icons-material';
import SectionAIAssistant from './SectionAIAssistant';

/**
 * Section Assistant Button Component
 * 
 * A floating button that can be positioned in section corners
 * to open the AI assistant for that specific section.
 */

const SectionAssistantButton = ({
  sectionTitle,
  sectionContent,
  allSections = [],
  position = 'bottom-right',
  variant = 'floating' // 'floating' or 'corner'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const getPositionStyles = () => {
    const baseStyles = {
      position: 'absolute',
      zIndex: 1000,
    };

    switch (position) {
      case 'top-left':
        return { ...baseStyles, top: 8, left: 8 };
      case 'top-right':
        return { ...baseStyles, top: 8, right: 8 };
      case 'bottom-left':
        return { ...baseStyles, bottom: 8, left: 8 };
      case 'bottom-right':
      default:
        return { ...baseStyles, bottom: 8, right: 8 };
    }
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  if (variant === 'floating') {
    return (
      <>
        <Fade in={true} timeout={500}>
          <Box sx={getPositionStyles()}>
            <Tooltip 
              title={`Ask AI about ${sectionTitle}`}
              placement="left"
              arrow
            >
              <IconButton
                onClick={handleToggle}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                sx={{
                  bgcolor: isOpen ? 'primary.main' : 'primary.main',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  width: 48,
                  height: 48,
                  opacity: 0.9,
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    color: 'white',
                    transform: 'scale(1.1)',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                    opacity: 1,
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <SmartToy />
              </IconButton>
            </Tooltip>
          </Box>
        </Fade>

        <SectionAIAssistant
          sectionTitle={sectionTitle}
          sectionContent={sectionContent}
          allSections={allSections}
          open={isOpen}
          onClose={() => setIsOpen(false)}
          position={position}
        />
      </>
    );
  }

  // Corner variant
  return (
    <>
      <Box sx={getPositionStyles()}>
        <Tooltip 
          title={`AI Assistant for ${sectionTitle}`}
          placement="left"
          arrow
        >
          <IconButton
            onClick={handleToggle}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              width: 40,
              height: 40,
              borderRadius: '50% 0 50% 0',
              '&:hover': {
                bgcolor: 'primary.dark',
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s ease-in-out',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}
          >
            <AutoAwesome fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <SectionAIAssistant
        sectionTitle={sectionTitle}
        sectionContent={sectionContent}
        allSections={allSections}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        position={position}
      />
    </>
  );
};

export default SectionAssistantButton;
